<script setup>
import { ref, reactive, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useAgentAvailabilityStore } from 'dashboard/stores/agentAvailability';

import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  // Agent's user_id. When omitted, edits the current user's own availability.
  userId: { type: Number, default: null },
});

const { t } = useI18n();
const store = useAgentAvailabilityStore();

// Display Monday-first; day_of_week stays 0=Sun..6=Sat (matches Ruby wday / JS).
const DAYS = [
  { wday: 1, key: 'MON' },
  { wday: 2, key: 'TUE' },
  { wday: 3, key: 'WED' },
  { wday: 4, key: 'THU' },
  { wday: 5, key: 'FRI' },
  { wday: 6, key: 'SAT' },
  { wday: 0, key: 'SUN' },
];
const KINDS = ['vacation', 'sick', 'holiday', 'other'];

const TIMEZONES =
  typeof Intl.supportedValuesOf === 'function'
    ? Intl.supportedValuesOf('timeZone')
    : [
        'UTC',
        'America/Argentina/Buenos_Aires',
        'Europe/Madrid',
        'America/New_York',
      ];

const uid = () => `${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;

// Recurring (every-other-week) days are always edited in terms of "this week" /
// "alternating week" rather than raw anchor dates — on save the anchor is re-stamped
// to the Monday of the current week, and on load the stored patterns are re-mapped
// onto "this week"/"alternating week" using the same parity math, so the agent never
// has to think about which historical week the anchor_date happens to fall in.
const mondayOf = date => {
  const d = new Date(date);
  const day = d.getDay();
  d.setDate(d.getDate() + (day === 0 ? -6 : 1) - day);
  d.setHours(0, 0, 0, 0);
  return d;
};
const isoDate = d => d.toISOString().slice(0, 10);
const parityFor = anchorIso => {
  const weeks = Math.round(
    (mondayOf(new Date()) - mondayOf(`${anchorIso}T00:00:00`)) /
      (7 * 24 * 60 * 60 * 1000)
  );
  return ((weeks % 2) + 2) % 2;
};

const isLoading = ref(true);
const isSaving = ref(false);
const resolvedUserId = ref(props.userId);

const form = reactive({
  timezone: 'UTC',
  days: DAYS.map(d => ({
    wday: d.wday,
    key: d.key,
    enabled: false,
    slots: [],
  })),
  timeOff: [],
});

const hydrate = vm => {
  form.timezone = vm.timezone || 'UTC';
  resolvedUserId.value = vm.userId ?? resolvedUserId.value;
  const toSlots = list =>
    (list || []).map(s => ({ id: uid(), start: s.start, end: s.end }));

  form.days = DAYS.map(d => {
    const match = (vm.weeklySchedule || []).find(
      w => Number(w.day_of_week) === d.wday
    );

    if (match?.recurrence?.interval_weeks === 2) {
      const parity = parityFor(match.recurrence.anchor_date);
      const patterns = match.recurrence.patterns || [];
      const slots = toSlots(patterns[parity]);
      const altSlots = toSlots(patterns[(parity + 1) % 2]);
      return {
        wday: d.wday,
        key: d.key,
        enabled: slots.length > 0 || altSlots.length > 0,
        recurring: true,
        slots,
        altSlots,
      };
    }

    const slots = toSlots(match?.slots);
    return {
      wday: d.wday,
      key: d.key,
      enabled: slots.length > 0,
      recurring: false,
      slots,
      altSlots: [],
    };
  });
  form.timeOff = (vm.timeOff || []).map(e => ({
    id: e.id || uid(),
    starts_on: e.starts_on,
    ends_on: e.ends_on,
    kind: e.kind || 'vacation',
    note: e.note || '',
  }));
};

onMounted(async () => {
  try {
    const vm = props.userId
      ? await store.fetch(props.userId)
      : await store.fetchMe();
    hydrate(vm);
  } catch (error) {
    useAlert(t('AGENT_AVAILABILITY.ALERTS.LOAD_ERROR'));
  } finally {
    isLoading.value = false;
  }
});

const toggleDay = day => {
  day.enabled = !day.enabled;
  if (day.enabled && day.slots.length === 0) {
    day.slots.push({ id: uid(), start: '09:00', end: '17:00' });
  }
};

const addSlot = (day, key = 'slots') => {
  day[key].push({ id: uid(), start: '09:00', end: '17:00' });
};
const removeSlot = (day, slotId, key = 'slots') => {
  day[key] = day[key].filter(s => s.id !== slotId);
  if (key === 'slots' && day.slots.length === 0) day.enabled = false;
};

// Toggling off drops the alternate pattern — a plain weekly day has nothing to hold it.
const toggleRecurring = day => {
  day.recurring = !day.recurring;
  if (!day.recurring) day.altSlots = [];
};

const addTimeOff = () => {
  const today = new Date().toISOString().slice(0, 10);
  form.timeOff.push({
    id: uid(),
    starts_on: today,
    ends_on: today,
    kind: 'vacation',
    note: '',
  });
};
const removeTimeOff = id => {
  form.timeOff = form.timeOff.filter(e => e.id !== id);
};

const save = async () => {
  isSaving.value = true;
  try {
    const buildPattern = slots =>
      slots.map(s => ({ start: s.start, end: s.end }));
    const weeklySchedule = form.days
      .filter(
        d =>
          d.enabled &&
          (d.recurring ? d.slots.length || d.altSlots.length : d.slots.length)
      )
      .map(d =>
        d.recurring
          ? {
              day_of_week: d.wday,
              recurrence: {
                interval_weeks: 2,
                anchor_date: isoDate(mondayOf(new Date())),
                patterns: [buildPattern(d.slots), buildPattern(d.altSlots)],
              },
            }
          : { day_of_week: d.wday, slots: buildPattern(d.slots) }
      );
    const targetId = props.userId ?? resolvedUserId.value;
    await store.update(targetId, {
      timezone: form.timezone,
      weeklySchedule,
      timeOff: form.timeOff.map(e => ({
        id: e.id,
        starts_on: e.starts_on,
        ends_on: e.ends_on,
        kind: e.kind,
        note: e.note,
      })),
    });
    useAlert(t('AGENT_AVAILABILITY.ALERTS.SAVED'));
  } catch (error) {
    useAlert(t('AGENT_AVAILABILITY.ALERTS.SAVE_ERROR'));
  } finally {
    isSaving.value = false;
  }
};

const fieldClass =
  'h-9 px-2 rounded-lg text-sm bg-n-alpha-black2 text-n-slate-12 border border-n-weak outline-none focus:border-n-brand';
</script>

<template>
  <div v-if="isLoading" class="py-10 text-center text-sm text-n-slate-11">
    {{ t('AGENT_AVAILABILITY.LOADING') }}
  </div>
  <div v-else class="flex flex-col gap-6">
    <!-- Timezone -->
    <label class="flex flex-col gap-1 max-w-sm">
      <span class="text-sm font-medium text-n-slate-12">
        {{ t('AGENT_AVAILABILITY.TIMEZONE') }}
      </span>
      <select v-model="form.timezone" :class="fieldClass">
        <option v-for="tz in TIMEZONES" :key="tz" :value="tz">{{ tz }}</option>
      </select>
    </label>

    <!-- Weekly schedule -->
    <div class="flex flex-col gap-2">
      <span class="text-sm font-medium text-n-slate-12">
        {{ t('AGENT_AVAILABILITY.WEEKLY.TITLE') }}
      </span>
      <span class="text-xs text-n-slate-11">
        {{ t('AGENT_AVAILABILITY.WEEKLY.HINT') }}
      </span>
      <div
        v-for="day in form.days"
        :key="day.wday"
        class="flex flex-col gap-2 p-3 rounded-lg border border-n-weak bg-n-solid-1"
      >
        <div class="flex items-center gap-3">
          <label class="flex items-center gap-2 w-28 cursor-pointer">
            <input
              type="checkbox"
              :checked="day.enabled"
              @change="toggleDay(day)"
            />
            <span class="text-sm font-medium text-n-slate-12">
              {{ t(`AGENT_AVAILABILITY.DAYS.${day.key}`) }}
            </span>
          </label>
          <span v-if="!day.enabled" class="text-xs text-n-slate-10">
            {{ t('AGENT_AVAILABILITY.WEEKLY.OFF') }}
          </span>
          <label
            v-if="day.enabled"
            class="flex items-center gap-1.5 text-xs text-n-slate-11 cursor-pointer"
          >
            <input
              type="checkbox"
              :checked="day.recurring"
              @change="toggleRecurring(day)"
            />
            {{ t('AGENT_AVAILABILITY.WEEKLY.REPEATS_BIWEEKLY') }}
          </label>
          <Button
            v-if="day.enabled"
            variant="ghost"
            color="slate"
            size="xs"
            icon="i-lucide-plus"
            :label="t('AGENT_AVAILABILITY.WEEKLY.ADD_SLOT')"
            class="ml-auto"
            @click="addSlot(day, 'slots')"
          />
        </div>
        <span
          v-if="day.enabled && day.recurring"
          class="text-xs font-medium text-n-slate-11 ltr:pl-28 rtl:pr-28"
        >
          {{ t('AGENT_AVAILABILITY.WEEKLY.THIS_WEEK') }}
        </span>
        <div
          v-for="slot in day.slots"
          :key="slot.id"
          class="flex items-center gap-2 ltr:pl-28 rtl:pr-28"
        >
          <input v-model="slot.start" type="time" :class="fieldClass" />
          <span class="text-xs text-n-slate-10">–</span>
          <input v-model="slot.end" type="time" :class="fieldClass" />
          <Button
            variant="ghost"
            color="ruby"
            size="xs"
            icon="i-lucide-trash-2"
            @click="removeSlot(day, slot.id, 'slots')"
          />
        </div>

        <template v-if="day.enabled && day.recurring">
          <div class="flex items-center gap-2 ltr:pl-28 rtl:pr-28">
            <span class="text-xs font-medium text-n-slate-11">
              {{ t('AGENT_AVAILABILITY.WEEKLY.ALTERNATE_WEEK') }}
            </span>
            <Button
              variant="ghost"
              color="slate"
              size="xs"
              icon="i-lucide-plus"
              :label="t('AGENT_AVAILABILITY.WEEKLY.ADD_SLOT')"
              @click="addSlot(day, 'altSlots')"
            />
          </div>
          <span
            v-if="!day.altSlots.length"
            class="text-xs text-n-slate-10 ltr:pl-28 rtl:pr-28"
          >
            {{ t('AGENT_AVAILABILITY.WEEKLY.OFF') }}
          </span>
          <div
            v-for="slot in day.altSlots"
            :key="slot.id"
            class="flex items-center gap-2 ltr:pl-28 rtl:pr-28"
          >
            <input v-model="slot.start" type="time" :class="fieldClass" />
            <span class="text-xs text-n-slate-10">–</span>
            <input v-model="slot.end" type="time" :class="fieldClass" />
            <Button
              variant="ghost"
              color="ruby"
              size="xs"
              icon="i-lucide-trash-2"
              @click="removeSlot(day, slot.id, 'altSlots')"
            />
          </div>
        </template>
      </div>
    </div>

    <!-- Time off -->
    <div class="flex flex-col gap-2">
      <div class="flex items-center justify-between">
        <span class="text-sm font-medium text-n-slate-12">
          {{ t('AGENT_AVAILABILITY.TIME_OFF.TITLE') }}
        </span>
        <Button
          variant="ghost"
          color="slate"
          size="xs"
          icon="i-lucide-plus"
          :label="t('AGENT_AVAILABILITY.TIME_OFF.ADD')"
          @click="addTimeOff"
        />
      </div>
      <span v-if="!form.timeOff.length" class="text-xs text-n-slate-10">
        {{ t('AGENT_AVAILABILITY.TIME_OFF.EMPTY') }}
      </span>
      <div
        v-for="entry in form.timeOff"
        :key="entry.id"
        class="flex flex-wrap items-center gap-2 p-3 rounded-lg border border-n-weak bg-n-solid-1"
      >
        <input v-model="entry.starts_on" type="date" :class="fieldClass" />
        <span class="text-xs text-n-slate-10">→</span>
        <input v-model="entry.ends_on" type="date" :class="fieldClass" />
        <select v-model="entry.kind" :class="fieldClass">
          <option v-for="k in KINDS" :key="k" :value="k">
            {{ t(`AGENT_AVAILABILITY.KINDS.${k.toUpperCase()}`) }}
          </option>
        </select>
        <input
          v-model="entry.note"
          type="text"
          :placeholder="t('AGENT_AVAILABILITY.TIME_OFF.NOTE')"
          class="flex-1 min-w-[8rem]"
          :class="[fieldClass]"
        />
        <Button
          variant="ghost"
          color="ruby"
          size="xs"
          icon="i-lucide-trash-2"
          @click="removeTimeOff(entry.id)"
        />
      </div>
    </div>

    <div class="flex justify-end">
      <Button
        color="blue"
        :label="t('AGENT_AVAILABILITY.SAVE')"
        :is-loading="isSaving"
        :disabled="isSaving"
        @click="save"
      />
    </div>
  </div>
</template>
